import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, X, Send, ChevronDown, Sprout, Bot, Maximize2, Minimize2, Volume2, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import API from '../api/axios';
import useOnlineStatus from '../hooks/useOnlineStatus';
import ModernSelect from './ModernSelect';
import '../styles/ChatBot.css';
import { useSocket } from '../contexts/SocketContext';
import { useAudio } from '../contexts/AudioContext';

// ─── Chat Listen Button ──────────────────────────────────────────────────────
const ChatListenButton = ({ text, index }) => {
    const { isPlaying, isLoading, playNewSequence, stopAudio, source, fullText, detectedLang } = useAudio();
    
    // Is THIS specific chat bubble playing?
    const isThisPlaying = isPlaying && source === 'chatbot' && fullText === text.replace(/[#*`]/g, '');

    const handleListen = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isThisPlaying) {
            stopAudio();
            return;
        }

        // Use detectedLang which is synced with the UI language (googtrans cookie)
        await playNewSequence(text.replace(/[#*`]/g, ''), detectedLang, 'chatbot');
    };

    return (
        <button 
            onClick={handleListen} 
            className={`chat-listen-btn ${isThisPlaying ? 'playing' : ''}`}
            disabled={isLoading && source === 'chatbot'}
            title="Listen to response"
        >
            {isLoading && source === 'chatbot' && !isThisPlaying ? (
                <Loader2 className="animate-spin" size={14} />
            ) : isThisPlaying ? (
                <>
                    <motion.div 
                        animate={{ scale: [1, 1.2, 1] }} 
                        transition={{ repeat: Infinity, duration: 1 }}
                    >
                        <Volume2 size={14} />
                    </motion.div>
                    <span>Playing</span>
                </>
            ) : (
                <>
                    <Volume2 size={14} />
                    <span>Listen</span>
                </>
            )}
        </button>
    );
};

const ChatBot = () => {
    const isOnline = useOnlineStatus();
    const { useSocketEvent } = useSocket();
    const [isOpen, setIsOpen] = useState(false);
    const [fields, setFields] = useState([]);
    const [selectedContext, setSelectedContext] = useState('general');
    const [messages, setMessages] = useState([
        { role: 'assistant', content: "Hello! I'm your Smart Agri-Advisor. How can I help you today?" }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    
    const messagesEndRef = useRef(null);

    // Listen for new messages via socket
    useSocketEvent('chat:new_message', (message) => {
        setMessages(prev => {
            // Avoid duplicate messages if this device already received it via API response
            const exists = prev.some(m => m._id === message._id || (m.content === message.content && m.role === message.role));
            if (exists) return prev;
            return [...prev, message];
        });
    });

    // Fetch user's fields on mount
    useEffect(() => {
        const fetchFields = async () => {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const token = userInfo?.token;
                if (!token) return;

                const { data } = await API.get('/api/fields');
                const fieldsArray = Array.isArray(data) ? data : (data.fields || []);
                setFields(fieldsArray);
            } catch (error) {
                console.error("Failed to fetch fields for chatbot context", error);
            }
        };

        if (isOpen && fields.length === 0) {
            fetchFields();
        }
    }, [isOpen, fields.length]);

    // Fetch chat history when context changes
    useEffect(() => {
        const fetchHistory = async () => {
            if (!isOpen) return;
            
            setIsLoading(true);
            try {
                const url = selectedContext === 'general' 
                    ? '/api/chat/history'
                    : `/api/chat/history?fieldId=${selectedContext}`;

                const { data } = await API.get(url);
                
                let greeting = "Hello! I'm your Smart Agri-Advisor. How can I help you today?";
                if (selectedContext !== 'general') {
                    const activeField = fields.find(f => f._id === selectedContext);
                    if (activeField?.activeCropName) {
                        greeting = `Hello! I'm your Smart Agri-Advisor. How can I help you today with your ${activeField.activeCropName}?`;
                    } else if (activeField) {
                        greeting = `Hello! I'm your Smart Agri-Advisor. How can I help you today with ${activeField.name}?`;
                    }
                }

                if (data && data.length > 0) {
                    setMessages(data);
                } else {
                    setMessages([{ role: 'assistant', content: greeting }]);
                }
            } catch (error) {
                console.error("Failed to fetch chat history", error);
                setMessages([{ role: 'assistant', content: "Hello! I'm your Smart Agri-Advisor. How can I help you today?" }]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistory();
    }, [selectedContext, isOpen, fields]);

    // Auto-scroll to bottom of messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e?.preventDefault();
        if (!input.trim()) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setIsLoading(true);

        try {
            const payload = {
                message: userMsg,
                fieldId: selectedContext === 'general' ? undefined : selectedContext,
            };

            const { data } = await API.post('/api/chat/ask', payload);
            
            setMessages(prev => {
                const exists = prev.some(m => m.content === data.content && m.role === data.role);
                if (exists) return prev;
                return [...prev, { role: 'assistant', content: data.content }];
            });
        } catch (error) {
            const errMsg = error.response?.data?.message || "Sorry, I encountered an error connecting to the server.";
            setMessages(prev => [...prev, { role: 'assistant', content: `**Error:** ${errMsg}` }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* FAB */}
            <motion.button 
                drag
                dragMomentum={false}
                dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={() => {
                    setTimeout(() => setIsDragging(false), 150);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`chatbot-fab ${isOpen ? 'active' : ''}`}
                onClick={() => {
                    if (!isDragging) setIsOpen(!isOpen);
                }}
                aria-label="Toggle Agri-Advisor Chat"
                style={{ touchAction: 'none' }}
            >
                {isOpen ? <ChevronDown size={28} /> : <MessageCircle size={28} />}
                {!isOpen && <span className="chatbot-notification-dot" />}
            </motion.button>

            {/* Chat Window */}
            <div className={`chatbot-window ${isOpen ? 'open' : ''} ${isFullScreen ? 'fullscreen' : ''}`}>
                <div className="chatbot-header">
                    <div className="chatbot-header-content">
                        <div className="chatbot-avatar-pulse">
                            <div className="chatbot-avatar">
                                <Sprout size={20} />
                            </div>
                            <span className={`status-dot ${isOnline ? 'online' : 'offline'}`} />
                        </div>
                        <div className="chatbot-header-text">
                            <h3>Agri-Advisor</h3>
                            <span className="chatbot-status-text">
                                {isOnline ? 'Direct Support AI' : 'Offline Mode'}
                            </span>
                        </div>
                    </div>
                    
                    <div className="chatbot-header-actions">
                        <button 
                            className="chatbot-action-btn" 
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                setIsFullScreen(!isFullScreen); 
                            }}
                            title={isFullScreen ? "Exit Fullscreen" : "Maximize"}
                        >
                            {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                        <button 
                            className="chatbot-action-btn close" 
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                setIsOpen(false); 
                                setIsFullScreen(false);
                            }}
                            title="Close Chat"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="chatbot-context-container">
                    <div className="chatbot-context-header">
                        <Bot size={14} className="text-primary" />
                        <span>How can I assist you today?</span>
                    </div>
                    <div className="chatbot-select-wrapper">
                        <ModernSelect
                            id="chat-context"
                            value={selectedContext}
                            onChange={(e) => setSelectedContext(e.target.value)}
                            options={[
                                { value: 'general', label: 'Global Consultation' },
                                ...fields.map(f => ({
                                    value: f._id,
                                    label: `Field: ${f.name} ${f.activeCropName ? `(${f.activeCropName})` : ''}`
                                }))
                            ]}
                            className="modern-select-sm"
                        />
                    </div>
                </div>

                <div className="chatbot-messages-container">
                    {messages.length === 0 && !isLoading && (
                        <div className="chatbot-empty-state">
                            <div className="empty-icon-wrap">
                                <Bot size={40} />
                            </div>
                            <h4>Your Agri-Advisor is ready</h4>
                            <p>Ask about crop health, fertilizer recommendations, or weather impacts.</p>
                        </div>
                    )}
                    
                    {messages.map((msg, idx) => (
                        <motion.div 
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.2, delay: idx * 0.05 }}
                            key={idx} 
                            className={`chat-message-row ${msg.role}`}
                        >
                            {msg.role === 'assistant' && (
                                <div className="message-avatar">
                                    <Bot size={14} />
                                </div>
                            )}
                            <div className="message-content-wrap">
                                <div className="message-bubble">
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    {msg.role === 'assistant' && (
                                        <div className="message-footer">
                                            <ChatListenButton 
                                                text={msg.content} 
                                                index={idx}
                                            />
                                        </div>
                                    )}
                                </div>
                                <span className="message-time">
                                    {new Date(msg.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </motion.div>
                    ))}
                    
                    {isLoading && (
                        <div className="chat-message-row assistant">
                            <div className="message-avatar">
                                <Bot size={14} />
                            </div>
                            <div className="message-bubble typing-bubble">
                                <div className="typing-dots">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="chatbot-footer">
                    <form className="chatbot-input-form" onSubmit={handleSend}>
                        <div className="input-group">
                            <input
                                type="text"
                                placeholder={isOnline ? "Message Agri-Advisor..." : "Connect to internet to chat..."}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                disabled={isLoading || !isOnline}
                                autoComplete="off"
                            />
                            <button 
                                type="submit" 
                                disabled={!input.trim() || isLoading || !isOnline}
                                className="chatbot-send-action"
                            >
                                <Send size={18} />
                            </button>
                        </div>
                    </form>
                    <p className="chatbot-disclaimer">AI can make mistakes. Verify critical info.</p>
                </div>
            </div>
        </>
    );
};

export default ChatBot;
